import { getWebInstrumentations, initializeFaro } from '@grafana/faro-web-sdk'
import { TracingInstrumentation } from '@grafana/faro-web-tracing'
import { dropHealthyFirestoreListens } from './faro'
import {
	CHECKOUT_COMMS_SERVICE_CONFIG,
	CHECKOUT_PAYMENT_SERVICE_CONFIG,
	CONTEXT_SERVICE_CONFIG,
	CURRENT_VERSION,
	ENV,
	FARO_CONFIG,
	PIMS_DATA_SERVICE_CONFIG,
	SCRATCHPAY_CASH_BACKEND_CONFIG
} from '../config'

// OpenTelemetry only sends `traceparent` to listed hosts, and ours are all cross-origin
const tracedBackends = [
	SCRATCHPAY_CASH_BACKEND_CONFIG.baseURL,
	CHECKOUT_PAYMENT_SERVICE_CONFIG.baseURL,
	CHECKOUT_COMMS_SERVICE_CONFIG.baseURL,
	CONTEXT_SERVICE_CONFIG.baseURL,
	PIMS_DATA_SERVICE_CONFIG.baseURL
]
	.filter((baseURL): baseURL is string => Boolean(baseURL))
	.map((baseURL) => new RegExp(`^${baseURL.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`))

// Set only by the staging and production CI variables, so those are the only builds that
// report. Either one missing leaves monitoring off rather than reporting under a wrong name.
const { collectorUrl, appName, samplingRate, bundleId } = FARO_CONFIG

// Share of sessions recorded, 0 to 1. Unset or unparseable records everything: a typo here
// should cost money, never leave us blind.
const rate = samplingRate ? Number(samplingRate) : 1
const sessionSamplingRate = rate >= 0 && rate <= 1 ? rate : 1

if (collectorUrl && appName) {
	if (bundleId) {
		const globalScope = globalThis as unknown as Record<string, string>
		globalScope[`__faroBundleId_${appName}`] = bundleId
	}

	initializeFaro({
		url: collectorUrl,
		app: {
			name: appName,
			version: CURRENT_VERSION,
			environment: ENV
		},
		instrumentations: [
			// CSP reporting off: prod runs Report-Only with GTM injecting tags, so it never stops
			...getWebInstrumentations({ enableContentSecurityPolicyInstrumentation: false }),
			new TracingInstrumentation({
				// Without this, unsampled sessions still send `traceparent` flagged unsampled and
				// the backend drops its own span — sampling here would silently cost backend traces
				omitTraceContextForUnsampledSessions: true,
				instrumentationOptions: { propagateTraceHeaderCorsUrls: tracedBackends }
			})
		],
		experimental: {
			trackNavigation: true
		},
		sessionTracking: {
			samplingRate: sessionSamplingRate
		},
		beforeSend: dropHealthyFirestoreListens,
		consoleInstrumentation: { consoleErrorAsLog: true },
		// Matched against `message + name + stack`, so an anchored regex can never match
		ignoreErrors: [
			// Harmless layout quirks, same as Sentry filters
			'ResizeObserver loop limit exceeded',
			'ResizeObserver loop completed with undelivered notifications',
			// Cross-origin scripts with no useful stack
			'Script error.',
			/chrome-extension:\/\//,
			/moz-extension:\/\//
		],
		// 3rd party analytics and chat widgets, whose polling would drown out our own requests
		ignoreUrls: [
			/googletagmanager\.com/,
			/analytics\.google\.com/,
			/www\.google-analytics\.com/,
			/doubleclick\.net/,
			/connect\.facebook\.net/,
			/facebook\.com\/tr/,
			/api\.mixpanel\.com/,
			/cdn\.mxpnl\.com/,
			/chat\.frontapp\.com/,
			/chat-assets\.frontapp\.com/,
			/chat\.onmaven\.app/,
			/gator\.stripe\.com/,
			/ingest\.sentry\.io/
		]
	})
}
